import { useCallback, useMemo } from 'react';
import orthopedicsData from '../../data/orthopedicsData.json';
import type { SpecialtyAttribute } from '../../services/videoApi/videoApi.types';
import {
  Specialty,
  VideoEditSpecialtySectionProps,
  UseVideoEditSpecialtySectionReturn,
  OrthopedicsData,
} from './types';

const BODY_PART_KEY = '部位';
const AO_CLASSIFICATION_KEY = 'AO分類';

const getBodyParts = () => {
  const typed = orthopedicsData as OrthopedicsData;
  return Array.isArray(typed?.bodyParts) ? typed.bodyParts : [];
};

const uniqueNonEmpty = (values: string[]): string[] =>
  Array.from(new Set(values.map(value => value.trim()).filter(Boolean)));

/**
 * VideoEditSpecialtySection コンポーネントのビジネスロジックを提供するカスタムフック
 *
 * 診療科ごとの専用入力欄は持たない。キーはサジェストするだけで、値は自由入力。
 * 部位・AO分類だけは既存の語彙（orthopedicsData.json）を値の候補として出す。
 */
export const useVideoEditSpecialtySection = (
  props: VideoEditSpecialtySectionProps
): UseVideoEditSpecialtySectionReturn => {
  const { specialties, selectedSpecialtyId, attributes, setAttributes, suggestedKeys } = props;

  // 選択中の診療科
  const chosenSpecialty = useMemo<Specialty | undefined>(() => {
    if (!Array.isArray(specialties)) return undefined;
    return specialties.find(s => s.id === selectedSpecialtyId);
  }, [specialties, selectedSpecialtyId]);

  // キーの候補：診療科のサジェスト → すでに付いているキー の順
  const keyOptions = useMemo(
    () => uniqueNonEmpty([...(suggestedKeys ?? []), ...attributes.map(attribute => attribute.attribute_key)]),
    [suggestedKeys, attributes]
  );

  // 値の候補
  const getValueOptions = useCallback(
    (attributeKey: string): string[] => {
      const key = attributeKey.trim();
      if (key === BODY_PART_KEY) {
        return getBodyParts().map(bodyPart => bodyPart.key);
      }
      if (key === AO_CLASSIFICATION_KEY) {
        // 部位が付いていればその部位の AO 分類だけに絞る
        const chosenBodyPart = attributes.find(attribute => attribute.attribute_key === BODY_PART_KEY)?.attribute_value.trim();
        const bodyParts = getBodyParts();
        const matched = chosenBodyPart ? bodyParts.find(bodyPart => bodyPart.key === chosenBodyPart) : undefined;
        if (matched) return matched.aoClassifications ?? [];
        return uniqueNonEmpty(bodyParts.flatMap(bodyPart => bodyPart.aoClassifications ?? []));
      }
      return [];
    },
    [attributes]
  );

  const addAttribute = useCallback(() => {
    // 未使用のサジェストキーがあれば、それを先頭に入れておく
    const usedKeys = new Set(attributes.map(attribute => attribute.attribute_key));
    const nextKey = (suggestedKeys ?? []).find(key => !usedKeys.has(key)) ?? '';
    setAttributes([...attributes, { attribute_key: nextKey, attribute_value: '' }]);
  }, [attributes, suggestedKeys, setAttributes]);

  const updateAttribute = useCallback(
    (index: number, patch: Partial<SpecialtyAttribute>) => {
      setAttributes(attributes.map((attribute, i) => (i === index ? { ...attribute, ...patch } : attribute)));
    },
    [attributes, setAttributes]
  );

  const removeAttribute = useCallback(
    (index: number) => {
      setAttributes(attributes.filter((_, i) => i !== index));
    },
    [attributes, setAttributes]
  );

  return {
    chosenSpecialty,
    keyOptions,
    getValueOptions,
    addAttribute,
    updateAttribute,
    removeAttribute,
  };
};

export default useVideoEditSpecialtySection;
